import { useRef } from 'react'
import { FileUp, Menu, Moon, Redo2, Sun, Undo2 } from 'lucide-react'

import { BatchButton } from '@/components/batch/batch-button'
import { AppLogo } from '@/components/layout/app-logo'
import { EditMenu } from '@/components/edit/edit-menu'
import { ExportButton } from '@/components/export/export-button'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import { useFileImport } from '@/hooks/use-file-import'
import { useUnits } from '@/hooks/use-units'
import type { UnitSystem } from '@/lib/format'
import { cn } from '@/lib/utils'
import {
  selectCanRedo,
  selectCanUndo,
  selectRedoLabel,
  selectUndoLabel,
  useActivityStore,
} from '@/store/activity-store'

/**
 * Top toolbar: branding, file import, global undo/redo (disabled per
 * `selectCanUndo`/`selectCanRedo`, tooltip names the action it would
 * reverse/re-apply), the "Edit" menu (M6, only rendered once an
 * activity is selected -- see `EditMenu`), the "Export" button (M7, same
 * selection-gated rule), the "Batch" button (M7, only appears once at
 * least one sidebar activity is ticked), the km/mi display-units toggle
 * and the light/dark theme toggle.
 *
 * Fits one row on a phone: below `md` the shell passes
 * `onShowActivities` (an "Activities" button for the `SidebarDrawer`,
 * which then holds the units/theme toggles instead), and below `sm` the
 * text buttons collapse to icons -- their labels stay as `sr-only` text,
 * so their accessible names never change.
 */
export function Toolbar({
  onShowActivities,
}: { onShowActivities?: (() => void) | undefined } = {}) {
  const canUndo = useActivityStore(selectCanUndo)
  const canRedo = useActivityStore(selectCanRedo)
  const undoLabel = useActivityStore(selectUndoLabel)
  const redoLabel = useActivityStore(selectRedoLabel)
  const undo = useActivityStore((s) => s.undo)
  const redo = useActivityStore((s) => s.redo)
  const undoHint = undoLabel != null ? `Undo: ${undoLabel} (Ctrl+Z)` : 'Undo (Ctrl+Z)'
  const redoHint = redoLabel != null ? `Redo: ${redoLabel} (Ctrl+Shift+Z)` : 'Redo (Ctrl+Shift+Z)'
  const activityCount = useActivityStore((s) => s.activities.length)
  const importFiles = useFileImport()
  const fileInputRef = useRef<HTMLInputElement>(null)

  return (
    // Wraps onto a second row on narrow screens rather than overflowing.
    <header className="border-border bg-card flex min-h-14 shrink-0 flex-wrap items-center gap-1 border-b px-2 py-2 sm:gap-2 sm:px-4">
      {onShowActivities != null && (
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Show activities (${activityCount} loaded)`}
          onClick={onShowActivities}
          className="relative"
        >
          <Menu className="h-4 w-4" aria-hidden="true" />
          {activityCount > 0 && (
            <span
              aria-hidden="true"
              className="bg-primary text-primary-foreground absolute top-0.5 right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-semibold"
            >
              {activityCount}
            </span>
          )}
        </Button>
      )}

      <div className="text-card-foreground flex items-center gap-2 font-semibold">
        <AppLogo className="text-primary h-5 w-5" aria-hidden="true" />
        <span className="max-sm:sr-only">FIT Editor</span>
      </div>

      <div className="bg-border mx-2 hidden h-6 w-px sm:block" aria-hidden="true" />

      <input
        ref={fileInputRef}
        type="file"
        accept=".fit"
        multiple
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          if (event.target.files != null && event.target.files.length > 0) {
            void importFiles(event.target.files)
          }
          event.target.value = ''
        }}
      />
      <Button
        variant="outline"
        size="sm"
        className="max-sm:w-8 max-sm:px-0"
        onClick={() => fileInputRef.current?.click()}
      >
        <FileUp className="h-4 w-4" aria-hidden="true" />
        <span className="max-sm:sr-only">Open file(s)</span>
      </Button>

      <div className="flex items-center gap-1">
        <Tooltip content={undoHint}>
          <Button variant="ghost" size="icon" aria-label="Undo" disabled={!canUndo} onClick={undo}>
            <Undo2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        </Tooltip>
        <Tooltip content={redoHint}>
          <Button variant="ghost" size="icon" aria-label="Redo" disabled={!canRedo} onClick={redo}>
            <Redo2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        </Tooltip>
      </div>

      <EditMenu />
      <ExportButton />
      <BatchButton />

      {onShowActivities == null && (
        <>
          <div className="flex-1" />
          <UnitsToggle />
          <ThemeToggle />
        </>
      )}
    </header>
  )
}

/** Light/dark theme toggle (persisted `ui.theme`). */
export function ThemeToggle() {
  const theme = useActivityStore((s) => s.ui.theme)
  const toggleTheme = useActivityStore((s) => s.toggleTheme)
  return (
    <Tooltip content={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}>
      <Button variant="ghost" size="icon" aria-label="Toggle color theme" onClick={toggleTheme}>
        {theme === 'dark' ? (
          <Sun className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Moon className="h-4 w-4" aria-hidden="true" />
        )}
      </Button>
    </Tooltip>
  )
}

const UNIT_OPTIONS: { units: UnitSystem; label: string; name: string }[] = [
  { units: 'metric', label: 'km', name: 'km (metric units)' },
  { units: 'imperial', label: 'mi', name: 'mi (imperial units)' },
]

/**
 * Compact "km | mi" segmented toggle for the display unit system (persisted
 * `ui.units`). Display only: switching never changes stored data or exports.
 */
export function UnitsToggle() {
  const units = useUnits()
  const setUnits = useActivityStore((s) => s.setUnits)
  return (
    <div
      role="group"
      aria-label="Display units"
      className="border-border inline-flex shrink-0 overflow-hidden rounded-md border"
    >
      {UNIT_OPTIONS.map((option) => (
        <button
          key={option.units}
          type="button"
          aria-pressed={units === option.units}
          aria-label={option.name}
          onClick={() => setUnits(option.units)}
          className={cn(
            'focus-visible:ring-ring px-2 py-1 text-xs font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset',
            units === option.units
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground bg-transparent',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
